import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  SimpleGrid,
  GridItem,
  NumberInput,
  NumberInputField,
} from "@chakra-ui/react";
import axiosInstance from "../../../helpers/axiosInstance";
import Translate from "../../../components/LanguageTranslation/Translate";

export default function AddTrainTickets({
  isOpen,
  onClose,
  addTripItem,
  defaultSubcategories = [],
  editItem = null,
  onEditSuccess = null,
}) {
  const [trainCompany, setTrainCompany] = useState("");
  const [originStation, setOriginStation] = useState("");
  const [destinationStation, setDestinationStation] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [arrivalDate, setArrivalDate] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        // Populate form with existing data for editing
        setTrainCompany(editItem.train_company || "");
        setOriginStation(editItem.origin_station || "");
        setDestinationStation(editItem.destination_station || "");
        setDepartureDate(editItem.departure_date ? new Date(editItem.departure_date).toISOString().split('T')[0] : "");
        setArrivalDate(editItem.arrival_date ? new Date(editItem.arrival_date).toISOString().split('T')[0] : "");
        setPrice(editItem.price?.amount?.toString() || "");
        setCurrency(editItem.price?.currency || "USD");
      } else {
        // Reset form for new train ticket
        setTrainCompany("");
        setOriginStation("");
        setDestinationStation("");
        setDepartureDate("");
        setArrivalDate("");
        setPrice("");
        setCurrency("USD");
      }
      setErrors({});
    }
  }, [isOpen, editItem]);

  const validate = () => {
    const e = {};
    if (!trainCompany.trim()) e.trainCompany = "Train company required";
    if (!originStation.trim()) e.originStation = "Origin station required";
    if (!destinationStation.trim()) e.destinationStation = "Destination station required";
    if (!departureDate) e.departureDate = "Departure date required";
    if (!arrivalDate) e.arrivalDate = "Arrival date required";
    if (!price || isNaN(price) || price <= 0) e.price = "Valid price required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      train_company: trainCompany,
      origin_station: originStation,
      destination_station: destinationStation,
      departure_date: new Date(departureDate).toISOString(),
      arrival_date: new Date(arrivalDate).toISOString(),
      price: parseFloat(price),
      currency,
    };

    // Add item_id to payload if editing
    if (editItem && editItem._id) {
      payload.item_id = editItem._id;
    }

    try {
      const response = await axiosInstance.post(
        "/bucket-list/add-manual-train-tickets",
        payload
      );
      if (response.data.success) {
        const trainTicketData = {
          train_company: trainCompany,
          origin_station: originStation,
          destination_station: destinationStation,
          departure_date: new Date(departureDate).toISOString(),
          arrival_date: new Date(arrivalDate).toISOString(),
          price: { amount: parseFloat(price), currency },
        };

        if (editItem && editItem._id) {
          // Update existing item
          const updatedItem = {
            ...editItem,
            data: trainTicketData,
          };
          if (onEditSuccess) {
            onEditSuccess(updatedItem);
          }
        } else {
          // Add new item
          const newItem = {
            _id: response.data.data._id,
            data: trainTicketData,
            category: "transportation",
            preference: "train",
            email: localStorage.getItem("email"),
          };

          if (newItem.category === "explore") {
            addTripItem({ ...newItem, data: { ...newItem } });
          } else {
            addTripItem(newItem);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>
          <Translate text={editItem ? "Edit Train Ticket" : "Add Train Ticket"} />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={2} spacing={4}>
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.trainCompany}>
                <FormLabel>
                  <Translate text="Train Company" />
                </FormLabel>
                <Input
                  value={trainCompany}
                  onChange={(e) => setTrainCompany(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.trainCompany} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.originStation}>
                <FormLabel>
                  <Translate text="Origin Station" />
                </FormLabel>
                <Input
                  value={originStation}
                  onChange={(e) => setOriginStation(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.originStation} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.destinationStation}>
                <FormLabel>
                  <Translate text="Destination Station" />
                </FormLabel>
                <Input
                  value={destinationStation}
                  onChange={(e) => setDestinationStation(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.destinationStation} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.departureDate}>
                <FormLabel>
                  <Translate text="Departure Date" />
                </FormLabel>
                <Input
                  type="date"
                  value={departureDate}
                  onChange={(e) => setDepartureDate(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.departureDate} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.arrivalDate}>
                <FormLabel>
                  <Translate text="Arrival Date" />
                </FormLabel>
                <Input
                  type="date"
                  value={arrivalDate}
                  onChange={(e) => setArrivalDate(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.arrivalDate} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.price}>
                <FormLabel>
                  <Translate text="Price" />
                </FormLabel>
                <NumberInput value={price} onChange={(_, v) => setPrice(v)}>
                  <NumberInputField />
                </NumberInput>
                <FormErrorMessage>
                  <Translate text={errors.price} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel>
                  <Translate text="Currency" />
                </FormLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="USD">USD $</option>
                  <option value="EUR">EUR €</option>
                  <option value="GBP">GBP £</option>
                </Select>
              </FormControl>
            </GridItem>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="customGreen" mr={3} onClick={handleSave}>
            <Translate text="Save" />
          </Button>
          <Button onClick={onClose}>
            <Translate text="Cancel" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
